import type {
    InventoryHealthRow,
    InventorySourcingModel,
} from '@api/replenishment-planning';

import type { GroupBy } from '../../../constants/replenishmentGrouping';

/**
 * Row shaping and roll-up for the Inventory health grid.
 *
 * Pure functions, kept out of the view so they can be tested directly — the ordering of
 * the SKUs inside a group went wrong once precisely because nothing exercised the tree,
 * and exporting them from the component file trips react-refresh/only-export-components.
 */
export const UNASSIGNED = '__unassigned__';


export type HealthRow = InventoryHealthRow & {
    rowKey: string;
    isGroup?: boolean;
    /** Group rows only — how many leaf rows rolled into it, for the label. */
    childCount?: number;
    subRows?: HealthRow[];
};

export const toRow = (row: InventoryHealthRow): HealthRow => ({
    ...row,
    rowKey: row.productId,
});

/** The value a row groups under, plus the label its parent shows. */
function groupValueOf(row: InventoryHealthRow, groupBy: GroupBy): { key: string; label: string } {
    switch (groupBy) {
        case 'SUPPLIER':
            return {
                key: row.vendorId ?? UNASSIGNED,
                label: row.vendorName || 'Unassigned supplier',
            };
        case 'CATEGORY':
            return { key: row.category ?? UNASSIGNED, label: row.category || 'Uncategorised' };
        case 'PRODUCT_GROUP':
            return {
                key: row.productGroup ?? UNASSIGNED,
                label: row.productGroup || 'No product group',
            };
        case 'TIER':
            return { key: row.tier ?? UNASSIGNED, label: row.tier || 'No tier' };
        case 'MATERIAL_TYPE':
            return {
                key: row.materialType ?? UNASSIGNED,
                label: row.materialType || 'No material type',
            };
        default:
            return { key: UNASSIGNED, label: '' };
    }
}

/**
 * Roll rows into parents on the chosen dimension.
 *
 * Additive columns (stock, each inbound leg, reorder point, shortfall, DRR) SUM. The ratio
 * column does not: group cover-days is recomputed as `group (stock + inbound) ÷ group DRR`,
 * never averaged across children — averaging a ratio over SKUs with wildly different run
 * rates produces a number that is true of no SKU and of the group.
 */
export function buildGroups(
    rows: InventoryHealthRow[],
    groupBy: GroupBy,
    subGroupBy: GroupBy = 'NONE',
    /**
     * Sort By ordering. Applied at every level — parent groups, nested sub-groups, and the
     * leaf SKUs inside them — so expanding a row never contradicts the ordering the toolbar
     * says is active.
     *
     * Omitted (Default Order), each level keeps the order it already had: groups by worst
     * shortfall, leaves alphabetically by name. The leaves' alphabetical order is the
     * server's, which sorts on SortBy.DEFAULT — nothing here re-sorts them, so a group you
     * open reads like a catalogue rather than a ranking.
     */
    compareRows?: (a: HealthRow, b: HealthRow) => number,
): HealthRow[] {
    const buckets = new Map<string, { label: string; rows: InventoryHealthRow[] }>();
    for (const row of rows) {
        const { key, label } = groupValueOf(row, groupBy);
        const bucket = buckets.get(key) ?? { label, rows: [] };
        bucket.rows.push(row);
        buckets.set(key, bucket);
    }

    return [...buckets]
        .map(([key, bucket]) =>
            // Children are either the leaf SKUs, or a second tier of group rows built by
            // exactly the same function — which is what makes the sub-group's arithmetic
            // identical to the parent's rather than a second implementation of it.
            makeGroupRow(`group:${groupBy}:${key}`, bucket.label, bucket.rows, () =>
                subGroupBy === 'NONE'
                    ? // map() already copies, so sorting in place cannot disturb the bucket
                      // the parent's own totals were summed from.
                      sortLeaves(bucket.rows.map(toRow), compareRows)
                    : buildGroups(
                          bucket.rows,
                          subGroupBy,
                          'NONE',
                          compareRows,
                      ).map((child) => ({
                          ...child,
                          // Namespaced by the parent: the same product group can appear
                          // under two categories, and identical row keys would make the
                          // grid expand both when either is clicked.
                          rowKey: `group:${groupBy}:${key}::${child.rowKey}`,
                      })),
            ),
        )
        .sort(
            compareRows ??
                ((a, b) =>
                    b.shortfall - a.shortfall || b.currentStock - a.currentStock),
        );
}

/**
 * Leaves under Default Order keep the server's alphabetical-by-name order rather than
 * borrowing the groups' worst-shortfall-first rule: a group you have deliberately opened
 * reads better as a catalogue than as a second ranking.
 */
function sortLeaves(
    leaves: HealthRow[],
    compareRows?: (a: HealthRow, b: HealthRow) => number,
): HealthRow[] {
    return compareRows ? leaves.sort(compareRows) : leaves;
}

/**
 * One parent row over a set of leaves.
 *
 * Additive columns (stock, each inbound leg, shortfall, DRR) SUM. The ratio column does not:
 * group cover-days is recomputed as `group (stock + inbound) ÷ group DRR`, never averaged
 * across children — averaging a ratio over SKUs with wildly different run rates produces a
 * number that is true of no SKU and of the group.
 */
function makeGroupRow(
    rowKey: string,
    label: string,
    rows: InventoryHealthRow[],
    buildSubRows: () => HealthRow[],
): HealthRow {
    const sum = (pick: (r: InventoryHealthRow) => number) =>
        rows.reduce((acc, r) => acc + pick(r), 0);
    const first = rows[0];
    const currentStock = sum((r) => r.currentStock);
    const inboundTotal = sum((r) => r.inbound.total);
    const drr = sum((r) => r.drr);
    // A null prior rate counts as a REAL ZERO here, unlike on a leaf row where it means "no
    // honest percentage exists". The difference is division versus addition: a SKU that sold
    // nothing in the prior window has no denominator of its own, but it genuinely contributed
    // zero to what the group was selling then. Dropping it instead would understate the base
    // and inflate the group's move; counting it says the group grew, which it did.
    const previousDrr = sum((r) => r.drrPrevious ?? 0);
    // Derive the vendor from the CHILDREN, not from which dimension we grouped on. A
    // supplier group trivially shares one vendor, but so can a category or tier group — and
    // tying this to groupBy meant those rows opened the PO modal with no supplier at all. A
    // group carries a vendor only when every child agrees on one; a PO cannot span two.
    const childVendorIds = new Set(rows.map((r) => r.vendorId));
    const sharedVendor = childVendorIds.size === 1 && first.vendorId ? first : null;
    // A group is placeable as one order only when every child agrees on the model.
    const models = new Set(rows.map((r) => r.sourcingModel));
    return {
        rowKey,
        isGroup: true,
        childCount: rows.length,
        productId: rowKey,
        sku: null,
        productName: label,
        category: null,
        productGroup: null,
        tier: null,
        materialType: null,
        unit: null,
        sourcingModel: (models.size === 1
            ? [...models][0]
            : 'UNMAPPED') as InventorySourcingModel,
        currentStock,
        // Inside currentStock, like on a leaf; null unless some child carries the figure.
        stockTransfer: rows.some((r) => r.stockTransfer != null)
            ? sum((r) => r.stockTransfer ?? 0)
            : null,
        inbound: {
            inTransitPo: sum((r) => r.inbound.inTransitPo),
            inTransitStn: sum((r) => r.inbound.inTransitStn),
            inProductionPo: sum((r) => r.inbound.inProductionPo),
            inProductionWo: sum((r) => r.inbound.inProductionWo),
            pendingPo: sum((r) => r.inbound.pendingPo ?? 0),
            pendingWo: sum((r) => r.inbound.pendingWo ?? 0),
            inTransit: sum((r) => r.inbound.inTransit),
            open: sum((r) => r.inbound.open),
            total: inboundTotal,
        },
        // Summed, like every other units column. It is a stock level — "this group should
        // trigger at N units" — and blanking it broke the one promise this view makes: that
        // its arithmetic checks out by eye. A row showing stock, inbound and shortfall with
        // a dash between them cannot be verified by the person reading it.
        //
        // Caveat the tooltip states: group shortfall is Σ max(0, ROP − available) per SKU,
        // NOT max(0, ΣROP − Σavailable). One SKU's surplus never cancels another's deficit,
        // so on a mixed group the shortfall is legitimately larger than the column
        // subtraction suggests.
        reorderPoint: sum((r) => r.reorderPoint),
        reorderTier: null,
        reorderLeadTimeDays: 0,
        reorderSafetyDays: 0,
        // A group has no single window. Its members can sit on different ones — that is
        // precisely what the operator is looking for when they group by tier — so reporting
        // one would name a policy that applies to some of the rows underneath and not others.
        // The editor refuses to open on a group for the same reason.
        ropWindowDays: 0,
        ropWindowSource: 'ORG_FALLBACK' as const,
        // Summed like every other rate: the group's threshold is the sum of its members'
        // thresholds, so the rate behind it is the sum of the rates those were sized on. Note
        // this is NOT the same as the group's `drr` whenever the members' windows differ from
        // the view's, which is exactly the discrepancy the grid is there to show.
        ropDrr: Number(sum((r) => r.ropDrr ?? 0).toFixed(2)),
        drr: Number(drr.toFixed(2)),
        // Recomputed from summed current and summed prior, never averaged over the children.
        // A group of one SKU at 2/day up 300% and one at 2000/day flat has NOT moved 150%.
        drrPrevious: previousDrr > 0 ? Number(previousDrr.toFixed(2)) : null,
        drrDeltaPct:
            previousDrr > 0
                ? Number((((drr - previousDrr) / previousDrr) * 100).toFixed(1))
                : null,
        shortfall: sum((r) => r.shortfall),
        // On-hand only, matching the leaf rows: inbound spans a 540-day horizon and would
        // report goods landing next year as cover held today. Recomputed from the group's
        // own stock and DRR rather than averaged across children — averaging a ratio over
        // SKUs with different run rates describes no SKU and no group.
        coverDays: drr > 0 ? Number((currentStock / drr).toFixed(1)) : null,
        vendorId: sharedVendor?.vendorId ?? null,
        vendorName: sharedVendor?.vendorName ?? null,
        moq: sharedVendor?.moq ?? null,
        stockInternal: sum((r) => r.stockInternal ?? 0),
        stockAtManufacturer: sum((r) => r.stockAtManufacturer ?? 0),
        inTransitInternal: sum((r) => r.inTransitInternal ?? 0),
        inTransitAtManufacturer: sum((r) => r.inTransitAtManufacturer ?? 0),
        fgDeficit: sum((r) => r.fgDeficit ?? 0),
        rmRequired: sum((r) => r.rmRequired ?? 0),
        subRows: buildSubRows(),
    };
}
